import { evaluateVital } from "@/lib/vitals-range";
import { computeMorseTotal, morseLevel, isMorseFilled } from "@/lib/morse";

// Compute clinical priority of a care program from its closing notes.
// Returns { level, reasons[] } where level is one of:
//   "red"    -> critical (action required now)
//   "yellow" -> follow-up (monitor / partial concern)
//   "green"  -> stable
//   "gray"   -> no closing data yet (cierre vacio)

export const LEVELS = {
  red:    { label: "Critico",     bg: "#FEE2E2", color: "#991B1B", dot: "#DC2626", border: "#DC2626" },
  yellow: { label: "Seguimiento", bg: "#FEF3C7", color: "#854D0E", dot: "#F59E0B", border: "#F59E0B" },
  green:  { label: "Estable",     bg: "#D1FAE5", color: "#065F46", dot: "#10B981", border: "#10B981" },
  gray:   { label: "Sin cierre",  bg: "#F1F5F9", color: "#475569", dot: "#94A3B8", border: "#CBD5E1" },
};

export const FILTER_OPTIONS = [
  { key: "all",    label: "Todos",       dot: "#1D4ED8" },
  { key: "red",    label: "Criticos",    dot: LEVELS.red.dot },
  { key: "yellow", label: "Seguimiento", dot: LEVELS.yellow.dot },
  { key: "green",  label: "Estables",    dot: LEVELS.green.dot },
  { key: "gray",   label: "Sin cierre",  dot: LEVELS.gray.dot },
];

export function computePriority(program) {
  const c = program?.closing || {};
  const incidents = c.incidents || {};
  const nutrition = c.nutrition || {};
  const vitals = c.vitals || {};
  const eva = Number.isFinite(c.eva_pain) ? c.eva_pain : 0;

  // Has any closing data been filled in?
  const anyIncidentEvaluated = Object.values(incidents).some(
    (v) => v === true || v === false
  );
  const hasClosingData =
    Boolean(c.condition) ||
    Boolean(c.emotion) ||
    eva > 0 ||
    anyIncidentEvaluated ||
    Boolean(vitals.ta || vitals.fc || vitals.temp || vitals.spo2) ||
    (vitals.readings || []).some((r) => r && (r.ta || r.fc || r.temp || r.spo2)) ||
    Boolean(c.observations) ||
    Boolean(c.closing_nurse) ||
    Boolean(nutrition.via || nutrition.appetite || nutrition.tolerance) ||
    Boolean(nutrition.breakfast || nutrition.lunch || nutrition.dinner) ||
    Boolean(c.elimination?.urine_method || c.elimination?.urine_times || c.elimination?.bristol) ||
    isMorseFilled(c.morse);

  if (!hasClosingData) {
    return { level: "gray", reasons: ["Notas de cierre vacias"] };
  }

  // ===== RED criteria =====
  const redReasons = [];
  if (c.condition === "Con deterioro") redReasons.push("Condicion: deterioro");
  if (eva >= 7) redReasons.push(`Dolor intenso (EVA ${eva}/10)`);
  const activeIncidents = Object.entries(incidents)
    .filter(([_, v]) => v === true)
    .map(([k]) => k);
  if (activeIncidents.length > 0) {
    redReasons.push(
      `${activeIncidents.length} incidencia${activeIncidents.length > 1 ? "s" : ""}`
    );
  }
  if (nutrition.vomit === true) redReasons.push("Vomito");
  // Severe diarrhea (Bristol 7) is concerning enough to escalate to red
  if (c.elimination?.bristol === "7") redReasons.push("Diarrea severa (Bristol 7)");
  // Morse: high fall risk -> red
  if (isMorseFilled(c.morse)) {
    const mTotal = computeMorseTotal(c.morse);
    const mLvl = morseLevel(mTotal);
    if (mLvl.key === "high") redReasons.push(`Riesgo alto caidas (Morse ${mTotal})`);
  }
  // Critical out-of-range vital signs in any of the 3 readings
  const readings = (vitals.readings || []).filter(Boolean);
  for (const r of readings) {
    const sp = evaluateVital("spo2", r.spo2);
    if (sp && sp.level === "low") {
      const n = Number(r.spo2);
      if (n < 90) redReasons.push(`SpO2 critico (${n}%)`);
    }
    const tp = evaluateVital("temp", r.temp);
    if (tp && tp.level === "high") {
      const n = Number(r.temp);
      if (n >= 38) redReasons.push(`Fiebre (${n}C)`);
    }
  }
  if (redReasons.length > 0) {
    return { level: "red", reasons: redReasons };
  }

  // ===== YELLOW criteria =====
  const yellowReasons = [];
  if (c.condition === "Sin cambios") yellowReasons.push("Sin cambios");
  if (eva >= 4 && eva <= 6) yellowReasons.push(`Dolor moderado (EVA ${eva}/10)`);
  if (nutrition.appetite === "Ausente") yellowReasons.push("Apetito ausente");
  else if (nutrition.appetite === "Disminuido") yellowReasons.push("Apetito disminuido");
  if (nutrition.tolerance === "Mala") yellowReasons.push("Tolerancia mala");
  else if (nutrition.tolerance === "Regular") yellowReasons.push("Tolerancia regular");
  if (nutrition.nausea === true) yellowReasons.push("Nausea");
  // Meals: 0 or 1/4 portions are concerning
  const MEAL_LABELS = { breakfast: "desayuno", lunch: "comida", dinner: "cena" };
  for (const k of ["breakfast", "lunch", "dinner"]) {
    const v = nutrition[k];
    if (v === "0") yellowReasons.push(`No comio ${MEAL_LABELS[k]}`);
    else if (v === "1/4") yellowReasons.push(`Solo 1/4 de ${MEAL_LABELS[k]}`);
  }
  if (c.emotion === "Ansioso/a" || c.emotion === "Agitado/a" || c.emotion === "Deprimido/a") {
    yellowReasons.push(`Estado: ${c.emotion.toLowerCase()}`);
  }
  // Bristol abnormal (constipation 1-2 / diarrhea 6) -> seguimiento
  const bristol = c.elimination?.bristol;
  if (bristol === "1" || bristol === "2") yellowReasons.push(`Bristol tipo ${bristol} (estrenimiento)`);
  else if (bristol === "6") yellowReasons.push("Bristol tipo 6 (diarrea leve)");
  // Morse medium-risk -> yellow
  if (isMorseFilled(c.morse)) {
    const mT = computeMorseTotal(c.morse);
    const mL = morseLevel(mT);
    if (mL.key === "medium") yellowReasons.push(`Riesgo medio caidas (Morse ${mT})`);
  }
  // Non-critical out-of-range vitals (taquicardia, bradicardia, febricula, SpO2 90-94, hipertension/hipotension)
  for (const r of readings) {
    for (const f of ["ta", "fc", "temp", "spo2"]) {
      const e = evaluateVital(f, r[f]);
      if (e && e.level !== "normal" && !yellowReasons.some((y) => y.includes(e.msg))) {
        yellowReasons.push(e.msg);
      }
    }
  }
  if (yellowReasons.length > 0) {
    return { level: "yellow", reasons: yellowReasons };
  }

  // ===== GREEN =====
  return {
    level: "green",
    reasons: [c.condition || "Estable", `EVA ${eva}/10`, "Sin incidencias"],
  };
}
